import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  isScaleLinked,
  syncLinkedScaleKeyframes,
  type Clip,
  type Keyframe,
  type Project,
} from '@cinegrid/core';
import { formatTimecode } from '../../timeline/ops';
import { useEditorStore } from '../../state/editorStore';

const LANE_H = 36;
const KF_EPS = 0.001;

const PROP_LABELS: Record<string, string> = {
  opacity: 'Opacity',
  positionX: 'Position X',
  positionY: 'Position Y',
  scaleX: 'Scale X',
  scaleY: 'Scale Y',
  rotation: 'Rotation',
  anchorX: 'Anchor X',
  anchorY: 'Anchor Y',
  exposure: 'Exposure',
  contrast: 'Contrast',
  saturation: 'Saturation',
  hue: 'Hue',
  temperature: 'Temperature',
  tint: 'Tint',
  shadows: 'Shadows',
  midtones: 'Midtones',
  highlights: 'Highlights',
  whites: 'Whites',
  blacks: 'Blacks',
  intensity: 'Intensity',
  blur: 'Blur',
  sharpen: 'Sharpen',
  vignette: 'Vignette',
  glow: 'Glow',
  noise: 'Noise',
};

type KeyGroup = {
  time: number;
  keys: Keyframe[];
};

function propLabel(property: string, scaleLinked: boolean): string {
  if (scaleLinked && property === 'scaleX') return 'Scale';
  return PROP_LABELS[property] ?? property;
}

function resolveActiveClip(
  project: Project,
  selection: string[],
  playhead: number
): Clip | undefined {
  const selected = selection
    .map((id) => project.clips.find((c) => c.id === id))
    .find((c): c is Clip => !!c);
  if (selected) return selected;

  const videoTrackIds = new Set(
    project.tracks.filter((t) => t.kind === 'video').map((t) => t.id)
  );
  const under = project.clips.filter(
    (c) =>
      videoTrackIds.has(c.trackId) &&
      playhead >= c.start &&
      playhead < c.start + c.duration
  );
  return under[under.length - 1];
}

function groupKeyframes(keyframes: Keyframe[], scaleLinked: boolean): KeyGroup[] {
  const visible = scaleLinked
    ? keyframes.filter((k) => k.property !== 'scaleY')
    : keyframes;
  const sorted = [...visible].sort((a, b) => a.time - b.time);
  const groups: KeyGroup[] = [];
  for (const k of sorted) {
    const last = groups[groups.length - 1];
    if (last && Math.abs(last.time - k.time) < KF_EPS) last.keys.push(k);
    else groups.push({ time: k.time, keys: [k] });
  }
  return groups;
}

function expandGroupIds(
  clip: Clip,
  group: KeyGroup,
  scaleLinked: boolean
): string[] {
  const ids = new Set(group.keys.map((k) => k.id));
  if (scaleLinked) {
    for (const k of clip.properties.keyframes) {
      if (k.property === 'scaleY' && Math.abs(k.time - group.time) < KF_EPS) {
        ids.add(k.id);
      }
    }
  }
  return [...ids];
}

function Diamond({
  selected,
  atPlayhead,
  stacked,
}: {
  selected?: boolean;
  atPlayhead?: boolean;
  stacked?: boolean;
}) {
  const fill = selected
    ? 'var(--cg-accent)'
    : atPlayhead
      ? 'color-mix(in srgb, var(--cg-accent) 55%, var(--cg-text-high))'
      : 'var(--cg-text-high)';
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden className="drop-shadow">
      <path
        d="M6 1.15 L10.85 6 L6 10.85 L1.15 6 Z"
        fill={fill}
        stroke={selected || atPlayhead ? 'var(--cg-accent)' : 'var(--cg-bg-panel)'}
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
      {stacked ? (
        <path
          d="M6 3.1 L8.9 6 L6 8.9 L3.1 6 Z"
          fill="none"
          stroke="var(--cg-bg-panel)"
          strokeWidth="1"
        />
      ) : null}
    </svg>
  );
}

export function TimelineKeyframeLane({
  widthPx,
  zoom,
  timeFromClientX,
}: {
  widthPx: number;
  zoom: number;
  timeFromClientX: (clientX: number) => number;
}) {
  const project = useEditorStore((s) => s.project);
  const selection = useEditorStore((s) => s.selection);
  const selectedKeyframeIds = useEditorStore((s) => s.selectedKeyframeIds);
  const playhead = useEditorStore((s) => s.playhead);
  const setPlayhead = useEditorStore((s) => s.setPlayhead);
  const selectKeyframes = useEditorStore((s) => s.selectKeyframes);
  const clearKeyframeSelection = useEditorStore((s) => s.clearKeyframeSelection);
  const deleteSelectedKeyframes = useEditorStore((s) => s.deleteSelectedKeyframes);
  const fps = project.settings.frameRate;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      if (!useEditorStore.getState().selectedKeyframeIds.length) return;
      e.preventDefault();
      e.stopPropagation();
      deleteSelectedKeyframes();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [deleteSelectedKeyframes]);

  const clip = useMemo(
    () => resolveActiveClip(project, selection, playhead),
    [project, selection, playhead]
  );

  const scaleLinked = isScaleLinked(clip?.properties);
  const groups = useMemo(
    () => (clip ? groupKeyframes(clip.properties.keyframes, scaleLinked) : []),
    [clip, scaleLinked]
  );

  const selectedSet = useMemo(() => new Set(selectedKeyframeIds), [selectedKeyframeIds]);

  const dragRef = useRef<{
    clipId: string;
    keyIds: Set<string>;
    startTimes: Map<string, number>;
    originLocal: number;
    moved: boolean;
  } | null>(null);

  const [marquee, setMarquee] = useState<{ a: number; b: number } | null>(null);
  const marqueeRef = useRef<{
    startAbs: number;
    startX: number;
    moved: boolean;
  } | null>(null);

  const applyDragTimes = (
    clipId: string,
    keyIds: Set<string>,
    startTimes: Map<string, number>,
    originLocal: number,
    nextOriginLocal: number
  ) => {
    const state = useEditorStore.getState();
    const live = state.project.clips.find((c) => c.id === clipId);
    if (!live) return;
    const linked = isScaleLinked(live.properties);
    const delta = nextOriginLocal - originLocal;
    let next = live.properties.keyframes.map((k) => {
      if (!keyIds.has(k.id)) return k;
      const base = startTimes.get(k.id) ?? k.time;
      return {
        ...k,
        time: Math.min(live.duration, Math.max(0, base + delta)),
      };
    });
    if (linked) next = syncLinkedScaleKeyframes(next);
    state.updateClipProperties(clipId, { keyframes: next });
    state.setPlayhead(live.start + Math.min(live.duration, Math.max(0, nextOriginLocal)));
  };

  const onGroupPointerDown = (e: ReactPointerEvent, group: KeyGroup) => {
    if (e.button !== 0 || !clip) return;
    e.preventDefault();
    e.stopPropagation();

    const groupIds = expandGroupIds(clip, group, scaleLinked);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    const alreadySelected = groupIds.some((id) => selectedSet.has(id));

    if (additive) {
      selectKeyframes(groupIds, 'toggle');
      // Don't start a drag on additive click — selection only
      if (!alreadySelected || e.shiftKey) {
        setPlayhead(clip.start + group.time);
      }
      return;
    }

    let moveIds = groupIds;
    if (alreadySelected) {
      // Drag all currently selected keys (plus this group's linked scaleY)
      moveIds = [...new Set([...selectedKeyframeIds, ...groupIds])];
    } else {
      selectKeyframes(groupIds, 'replace');
    }

    const startTimes = new Map<string, number>();
    for (const k of clip.properties.keyframes) {
      if (moveIds.includes(k.id)) startTimes.set(k.id, k.time);
    }

    dragRef.current = {
      clipId: clip.id,
      keyIds: new Set(moveIds),
      startTimes,
      originLocal: group.time,
      moved: false,
    };
    useEditorStore.getState().beginHistoryTransaction();

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (Math.abs(ev.clientX - e.clientX) > 2) drag.moved = true;
      const abs = timeFromClientX(ev.clientX);
      const live = useEditorStore.getState().project.clips.find((c) => c.id === drag.clipId);
      if (!live) return;
      applyDragTimes(
        drag.clipId,
        drag.keyIds,
        drag.startTimes,
        drag.originLocal,
        abs - live.start
      );
    };
    const onUp = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      useEditorStore.getState().endHistoryTransaction();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (!drag?.moved && clip) {
        setPlayhead(clip.start + group.time);
        if (!alreadySelected) selectKeyframes(groupIds, 'replace');
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const onLanePointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-keyframe-mark]')) return;
    e.stopPropagation();
    e.preventDefault();

    const startAbs = timeFromClientX(e.clientX);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    if (!additive) clearKeyframeSelection();
    setPlayhead(startAbs);

    marqueeRef.current = { startAbs, startX: e.clientX, moved: false };
    setMarquee(null);

    const onMove = (ev: PointerEvent) => {
      const m = marqueeRef.current;
      if (!m || !clip) return;
      if (Math.abs(ev.clientX - m.startX) > 3) m.moved = true;
      if (!m.moved) return;
      const endAbs = timeFromClientX(ev.clientX);
      setMarquee({ a: Math.min(m.startAbs, endAbs), b: Math.max(m.startAbs, endAbs) });
    };
    const onUp = (ev: PointerEvent) => {
      const m = marqueeRef.current;
      marqueeRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (!m?.moved || !clip) {
        setMarquee(null);
        return;
      }
      const endAbs = timeFromClientX(ev.clientX);
      const t0 = Math.min(m.startAbs, endAbs);
      const t1 = Math.max(m.startAbs, endAbs);
      const ids: string[] = [];
      for (const group of groups) {
        const abs = clip.start + group.time;
        if (abs >= t0 - KF_EPS && abs <= t1 + KF_EPS) {
          ids.push(...expandGroupIds(clip, group, scaleLinked));
        }
      }
      selectKeyframes(ids, additive ? 'add' : 'replace');
      setMarquee(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const marqueeStyle =
    marquee && clip
      ? {
          left: marquee.a * zoom,
          width: Math.max(2, (marquee.b - marquee.a) * zoom),
        }
      : null;

  return (
    <div
      className="relative flex border-b border-hairline bg-[color-mix(in_srgb,var(--cg-bg-raised)_35%,transparent)]"
      style={{ height: LANE_H }}
      data-keyframe-lane
    >
      <div className="sticky left-0 z-10 flex w-[88px] shrink-0 flex-col justify-center border-r border-hairline bg-panel px-2">
        <span className="truncate text-2xs font-semibold text-text-high">Keyframes</span>
        <span className="truncate text-2xs text-text-low" title={clip?.name}>
          {clip
            ? selectedKeyframeIds.length
              ? `${selectedKeyframeIds.length} selected`
              : groups.length
                ? clip.name
                : 'No keyframes'
            : 'Select a clip'}
        </span>
      </div>
      <div
        className="relative flex-1 cursor-default"
        style={{ width: widthPx }}
        onPointerDown={onLanePointerDown}
      >
        {clip && (
          <div
            className="pointer-events-none absolute top-2 bottom-2 rounded-md bg-accent/10"
            style={{
              left: clip.start * zoom,
              width: Math.max(8, clip.duration * zoom),
            }}
          />
        )}
        {marqueeStyle && (
          <div
            className="pointer-events-none absolute top-1 bottom-1 z-[1] rounded-sm border border-accent/80 bg-accent/20"
            style={marqueeStyle}
          />
        )}
        {clip &&
          groups.map((group) => {
            const abs = clip.start + group.time;
            const atPlayhead = Math.abs(playhead - abs) < 1 / fps / 2;
            const stacked = group.keys.length > 1;
            const selected = group.keys.some((k) => selectedSet.has(k.id));
            const labels = group.keys
              .map((k) => propLabel(k.property, scaleLinked))
              .join(', ');
            return (
              <button
                key={`${group.time}-${group.keys.map((k) => k.id).join('-')}`}
                type="button"
                data-keyframe-mark
                aria-selected={selected}
                title={`${labels} · ${formatTimecode(abs, fps)} — click to select, drag to move`}
                className={`absolute top-1/2 z-[2] flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-md ${
                  selected ? 'bg-accent/20' : 'hover:bg-raised/80'
                }`}
                style={{ left: abs * zoom }}
                onPointerDown={(e) => onGroupPointerDown(e, group)}
              >
                <Diamond selected={selected} atPlayhead={atPlayhead} stacked={stacked} />
              </button>
            );
          })}
      </div>
    </div>
  );
}
